import { Injectable } from '@angular/core';

import { JepCookiesService } from '@jepto/shared/services/jep-cookies/jep-cookies.service';

import { safeFetchAuthSession } from '@jepto/shared/utils/fetch-auth-session.util';
import {
	getEmailFromIdTokenPayload,
	normalizeAccessTokenIdentityClaims,
} from '@jepto/shared/utils/id-token-payload.util';
import {
	BehaviorSubject,
	combineLatest,
	defer,
	from,
	Observable,
	OperatorFunction,
} from 'rxjs';
import { filter, take } from 'rxjs/operators';

/**
 * App-wide singleton: the session is fetched once and every injector (lazy
 * modules included) reads the same resolved payload. Do not re-provide it in
 * an NgModule or component, or that scope starts over with a null payload.
 */
@Injectable({ providedIn: 'root' })
export class JepDecodedJWTService {
	private decodedJWTSubject = new BehaviorSubject<any>(null);

	/**
	 * Emits the Cognito access-token payload whenever the session resolves.
	 * May emit `null` when not logged in.
	 * Use to defer UI that depends on `sub` until after the session resolves.
	 */
	readonly decodedJwt$: Observable<any | null> =
		this.decodedJWTSubject.asObservable();
	private fetchingToken = false;

	constructor(private jepCookiesService: JepCookiesService) {
		from(safeFetchAuthSession()).subscribe(session => {
			this.#applyAccessTokenPayload(
				session?.tokens?.accessToken?.payload
			);
		});
	}

	/**
	 * Emits the access-token payload once it is available, then completes.
	 *
	 * Reads through `getDecodedJWT()` first so a session that failed to resolve
	 * during construction is re-fetched; `safeFetchAuthSession()` swallows its
	 * errors, so that retry is the only recovery path.
	 */
	decodedJwtOnce$(): Observable<any> {
		return defer(() => {
			this.getDecodedJWT();
			return this.decodedJWTSubject;
		}).pipe(filter(Boolean), take(1));
	}

	/**
	 * Pairs every source emission with the resolved access-token payload.
	 *
	 * Subscribers that match the signed-in user against a shared list must hold
	 * until the Cognito session lands, otherwise the list emits first and they
	 * index into a null payload (JEP-10542).
	 */
	withDecodedJwt<T>(): OperatorFunction<T, [T, any]> {
		return source => combineLatest([source, this.decodedJwtOnce$()]);
	}

	/**
	 * Returns the signed-in user's email, or null when the session has not
	 * resolved yet. Callers that key storage on it must handle the null.
	 */
	getEmail(): string | null {
		return getEmailFromIdTokenPayload(this.getDecodedJWT());
	}

	/**
	 * Returns the Cognito username from the access-token payload, or null.
	 */
	getCognitoUsername(): string | null {
		const payload = this.getDecodedJWT();
		const username = payload?.['cognito:username'] ?? payload?.['username'];
		return username == null ? null : String(username);
	}

	/**
	 * Returns the Cognito username or throws when the payload is unavailable.
	 */
	requireCognitoUsername(): string {
		const username = this.getCognitoUsername();
		if (!username) {
			throw new Error(
				'Cognito username unavailable: access token is not initialized'
			);
		}
		return username;
	}

	/**
	 * Returns the cached Cognito access-token payload.
	 *
	 * May return null when:
	 * - The user is not authenticated.
	 * - The token has not been fetched/initialised yet (e.g. on first load).
	 */
	getDecodedJWT() {
		if (this.decodedJWTSubject.value) {
			return this.decodedJWTSubject.value;
		}

		if (!this.fetchingToken) {
			this.fetchingToken = true;
			setTimeout(() => {
				from(safeFetchAuthSession()).subscribe(
					session => {
						this.#applyAccessTokenPayload(
							session?.tokens?.accessToken?.payload
						);
					},
					err => {
						console.error('fetchAuthSession', err);
						this.fetchingToken = false;
					},
					() => {
						this.fetchingToken = false;
					}
				);
			});
		}
		return this.decodedJWTSubject.value;
	}

	#applyAccessTokenPayload(
		payload: Record<string, unknown> | undefined
	): void {
		if (!payload) {
			return;
		}
		this.decodedJWTSubject.next(
			normalizeAccessTokenIdentityClaims(payload)
		);
		const sub = payload['sub'];
		if (sub != null) {
			localStorage.setItem('lau', String(sub));
		}
		const accountId = payload['custom:accountId'];
		if (accountId != null) {
			this.jepCookiesService.setCookie('accountId', String(accountId));
		}
	}
}
